import round from 'lodash/round';
import { formatPercentage } from '@cast/utils';

import { MetricCard } from './MetricCard';

import {
  EphemeralStorageMetrics,
  ResourceUsageMetrics,
} from '../../../../types';
import { Typography, Stack } from '@cast/design-system';

type Props = {
  cpuMetrics: ResourceUsageMetrics;
  memoryMetrics: ResourceUsageMetrics;
  ephemeralStorageMetrics?: EphemeralStorageMetrics;
};

export const UtilizationSection = ({
  cpuMetrics,
  memoryMetrics,
  ephemeralStorageMetrics,
}: Props) => {
  return (
    <Stack gap={16} data-testid="node-utilization-section">
      <Typography variant="h5">Utilization</Typography>
      <Stack direction="row" gap={16} flexWrap="wrap">
        <MetricCard
          title="CPU"
          acronym="CPU"
          metrics={cpuMetrics}
          outerLayer={[
            {
              color: 'blue.200',
              value: cpuMetrics.requested,
            },
          ]}
          metricsBoxed={[
            {
              title: 'REQUESTED',
              markerColor: 'blue.200',
              primaryValue: round(cpuMetrics.requested, 2),
              secondaryValue: formatPercentage(
                cpuMetrics.capacity
                  ? (cpuMetrics.requested / cpuMetrics.capacity) * 100
                  : null,
                { roundBy: 0 }
              ),
            },
            {
              title: 'ALLOCATABLE',
              markerColor: 'grey.800',
              primaryValue: round(cpuMetrics.allocatable, 2),
              secondaryValue: 'CPU',
            },
            {
              title: 'OVERHEAD',
              markerColor: 'grey.200',
              primaryValue: round(
                cpuMetrics.capacity - cpuMetrics.allocatable,
                2
              ),
              secondaryValue: formatPercentage(
                cpuMetrics.capacity
                  ? ((cpuMetrics.capacity - cpuMetrics.allocatable) /
                      cpuMetrics.capacity) *
                      100
                  : null,
                { roundBy: 0 }
              ),
            },
          ]}
          testId="node-utilization-cpu"
        />
        <MetricCard
          title="Memory"
          acronym="GiB"
          metrics={memoryMetrics}
          outerLayer={[
            {
              color: 'blue.200',
              value: memoryMetrics.requested,
            },
          ]}
          metricsBoxed={[
            {
              title: 'REQUESTED',
              markerColor: 'blue.200',
              primaryValue: round(memoryMetrics.requested, 2),
              secondaryValue: formatPercentage(
                memoryMetrics.capacity
                  ? (memoryMetrics.requested / memoryMetrics.capacity) * 100
                  : null,
                { roundBy: 0 }
              ),
            },
            {
              title: 'ALLOCATABLE',
              markerColor: 'grey.800',
              primaryValue: round(memoryMetrics.allocatable, 2),
              secondaryValue: 'GiB',
            },
            {
              title: 'OVERHEAD',
              markerColor: 'grey.200',
              primaryValue: round(
                memoryMetrics.capacity - memoryMetrics.allocatable,
                2
              ),
              secondaryValue: formatPercentage(
                memoryMetrics.capacity
                  ? ((memoryMetrics.capacity - memoryMetrics.allocatable) /
                      memoryMetrics.capacity) *
                      100
                  : null,
                { roundBy: 0 }
              ),
            },
          ]}
          testId="node-utilization-memory"
        />
        {ephemeralStorageMetrics && (
          <MetricCard
            title="Ephemeral Storage"
            acronym="GiB"
            metrics={ephemeralStorageMetrics}
            outerLayer={[
              {
                color: 'green.100',
                value: ephemeralStorageMetrics.used,
              },
              {
                color: 'blue.200',
                value:
                  ephemeralStorageMetrics.requested >
                  ephemeralStorageMetrics.used
                    ? ephemeralStorageMetrics.requested -
                      ephemeralStorageMetrics.used
                    : 0,
              },
            ]}
            metricsBoxed={[
              {
                title: 'REQUESTED',
                markerColor: 'blue.200',
                primaryValue: round(ephemeralStorageMetrics.requested, 2),
                secondaryValue: formatPercentage(
                  ephemeralStorageMetrics.capacity
                    ? (ephemeralStorageMetrics.requested /
                        ephemeralStorageMetrics.capacity) *
                        100
                    : null,
                  { roundBy: 0 }
                ),
              },
              {
                title: 'USED',
                markerColor: 'green.100',
                primaryValue: round(ephemeralStorageMetrics.used, 2),
                secondaryValue: formatPercentage(
                  ephemeralStorageMetrics.capacity
                    ? (ephemeralStorageMetrics.used /
                        ephemeralStorageMetrics.capacity) *
                        100
                    : null,
                  { roundBy: 0 }
                ),
              },
              {
                title: 'ALLOCATABLE',
                markerColor: 'grey.800',
                primaryValue: round(ephemeralStorageMetrics.allocatable, 2),
                secondaryValue: 'GiB',
              },
              {
                title: 'OVERHEAD',
                markerColor: 'grey.200',
                primaryValue: round(
                  ephemeralStorageMetrics.capacity -
                    ephemeralStorageMetrics.allocatable,
                  2
                ),
                secondaryValue: formatPercentage(
                  ephemeralStorageMetrics.capacity
                    ? ((ephemeralStorageMetrics.capacity -
                        ephemeralStorageMetrics.allocatable) /
                        ephemeralStorageMetrics.capacity) *
                        100
                    : null,
                  { roundBy: 0 }
                ),
              },
            ]}
            testId="node-utilization-ephemeral-storage"
          />
        )}
      </Stack>
    </Stack>
  );
};

const UtilizationSectionSkeleton = () => {
  return (
    <Stack gap={16}>
      <Typography variant="h5">Utilization</Typography>
      <Stack direction="row" gap={16} flexWrap="wrap">
        {Array.from({ length: 3 }).map((_, index) => (
          <MetricCard.Skeleton key={index} />
        ))}
      </Stack>
    </Stack>
  );
};

UtilizationSection.Skeleton = UtilizationSectionSkeleton;
